'use client';

import { useSearchResults } from '@/components/pages/search/use-search-results';
import { ProductCard } from '@/components/product-card';
import type { ProductCardProps } from '@/lib/types';
import { serializeProductCardToElevarProduct } from '@/lib/utils';
import { useElevarEvents } from '@veranda/shared/elevar';
import {
  SearchDialogShared,
  useSearchDialogState,
} from '@veranda/shared/layout/search-dialog';
import { X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

export function SearchDialog() {
  const { isOpen, setIsOpen } = useSearchDialogState();
  const [search, setSearch] = useState('');
  const { trackViewSearchResults } = useElevarEvents();
  const _router = useRouter();

  const { data, isLoading } = useSearchResults({
    query: search,
    page: 1,
    enabled: isOpen,
  });

  const stableTrackViewSearchResults = useCallback(
    (data: Parameters<typeof trackViewSearchResults>[0]) => {
      trackViewSearchResults(data);
    },
    [trackViewSearchResults]
  );

  const handleSearchSubmit = useCallback(() => {
    if (search.trim()) {
      // Close dialog first, then navigate
      setIsOpen(false);
      // Use window.location to ensure navigation happens
      const searchUrl = `/search?q=${encodeURIComponent(search)}`;
      window.location.href = searchUrl;
    }
  }, [search, setIsOpen]);

  useEffect(() => {
    if (data) {
      // Delay to ensure dl_user_data from route tracker fires first
      setTimeout(() => {
        stableTrackViewSearchResults({
          impressions: data.map((product, index) => {
            return serializeProductCardToElevarProduct({
              // ? Casting because the embeddings index returns ProductCardProps + _score
              product: product as ProductCardProps,
              position: index + 1,
              list: 'search',
            });
          }),
        });
      }, 200);
    }
  }, [data, stableTrackViewSearchResults]);

  return (
    <SearchDialogShared.Root open={isOpen} onOpenChange={setIsOpen}>
      <SearchDialogShared.Content>
        <SearchDialogShared.Container>
          <SearchDialogShared.Header>
            <SearchDialogShared.Input
              value={search}
              onChange={setSearch}
              isLoading={isLoading}
              onSubmit={handleSearchSubmit}
              className="text-primary placeholder:text-primary/50"
            />
            <div className="flex justify-end">
              <SearchDialogShared.Close type="button">
                <span className="sr-only">Close Search</span>
                <X
                  className="size-6 text-primary"
                  id="search-dialog-close-icon"
                />
              </SearchDialogShared.Close>
            </div>
          </SearchDialogShared.Header>
          <hr className="border-t" />
          <SearchDialogShared.Results>
            <SearchDialogShared.ResultsHeader>
              <SearchDialogShared.ResultsTitle className="typography-outfit-h3 text-primary">
                Search Results
              </SearchDialogShared.ResultsTitle>
              <SearchDialogShared.ResultsViewAll
                query={search}
                className="text-primary"
              >
                View all
              </SearchDialogShared.ResultsViewAll>
            </SearchDialogShared.ResultsHeader>
            <SearchDialogShared.ResultsGrid>
              {data?.map((product, index) => (
                <ProductCard
                  key={product._score as number}
                  // ? Casting because the embeddings index returns ProductCardProps + _score
                  product={product as ProductCardProps}
                  index={index}
                />
              ))}
            </SearchDialogShared.ResultsGrid>
          </SearchDialogShared.Results>
        </SearchDialogShared.Container>
      </SearchDialogShared.Content>
    </SearchDialogShared.Root>
  );
}
